import { useEffect, useMemo, useState } from "react";
import { SupportLookOnlyNote } from "@/components/SupportLookOnlyNote";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtLog } from "@/lib/tz";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Lock, Loader2, RotateCcw, Users, Save, Plus, Trash2 } from "lucide-react";

const CRIMSON = "var(--brand)";
const LEVELS = ["view", "edit", "manage"];
const LEVEL_LABEL = { none: "None", view: "View", edit: "Edit", manage: "Manage" };

// The actions a level switches on: every grantable action at or below it.
function presetFor(area, level) {
  if (level === "none") return [];
  const top = LEVELS.indexOf(level);
  return area.actions.filter((a) => !a.admin_only && LEVELS.indexOf(a.level) <= top).map((a) => a.key);
}

// Levels worth offering for an area (one with only Edit actions has no View step).
function levelsFor(area) {
  const have = new Set(area.actions.filter((a) => !a.admin_only).map((a) => a.level));
  return ["none", ...LEVELS.filter((l) => have.has(l))];
}

function currentLevel(area, granted) {
  const mine = area.actions.filter((a) => !a.admin_only && granted.has(a.key)).map((a) => a.key);
  if (mine.length === 0) return "none";
  for (const l of levelsFor(area)) {
    const p = presetFor(area, l);
    if (p.length === mine.length && p.every((k) => granted.has(k))) return l;
  }
  return "custom";
}

function LevelPicker({ area, granted, disabled, onPick }) {
  const cur = currentLevel(area, granted);
  return (
    <div className="flex items-center gap-2">
      {cur === "custom" && <Badge className="border bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Custom</Badge>}
      <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" role="radiogroup" aria-label={`${area.label} access`}>
        {levelsFor(area).map((l) => {
          const on = cur === l;
          return (
            <button key={l} type="button" role="radio" aria-checked={on} disabled={disabled}
              onClick={() => onPick(l)} data-testid={`level-${area.key}-${l}`}
              className={`px-2.5 h-7 text-[11px] font-medium rounded transition-colors disabled:cursor-not-allowed ${
                on ? "bg-white text-slate-900 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-800 disabled:hover:text-slate-500"}`}>
              {LEVEL_LABEL[l]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ActionRow({ action, checked, disabled, onToggle, asSwitch }) {
  const locked = action.admin_only;
  const id = `perm-${action.key}`;
  return (
    <label htmlFor={id} className={`flex items-start gap-2.5 py-1.5 ${disabled || locked ? "cursor-not-allowed" : "cursor-pointer"}`}>
      {asSwitch
        ? <Switch id={id} checked={checked} disabled={disabled || locked} onCheckedChange={onToggle} data-testid={id} className="mt-0.5" />
        : <Checkbox id={id} checked={checked} disabled={disabled || locked} onCheckedChange={(v) => onToggle(!!v)} data-testid={id}
            className="mt-0.5 border-slate-300 data-[state=checked]:bg-brand data-[state=checked]:border-brand" />}
      <span className={`text-xs leading-5 ${disabled || locked ? "text-slate-400" : "text-slate-700"}`}>
        {action.label}
        {locked && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-slate-400"><Lock className="h-3 w-3" />Admin only</span>}
        {!asSwitch && !locked && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-300">{action.level}</span>}
      </span>
    </label>
  );
}

export default function RolesSettings() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);       // Set of keys being edited
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState("");
  const [meta, setMeta] = useState({ label: "", description: "" }); // custom role name/description being edited
  const [creating, setCreating] = useState(false);

  const select = (d, id) => {
    const r = d.roles.find((x) => x.id === id);
    setSelected(id);
    setDraft(new Set(r?.permissions || []));
    setMeta({ label: r?.label || "", description: r?.description || "" });
  };
  const apply = (d, keepId) => {
    setData(d);
    const id = [keepId, selected].find((x) => x && d.roles.some((r) => r.id === x))
      || d.roles.find((r) => !r.locked)?.id || d.roles[0]?.id;
    select(d, id);
  };
  useEffect(() => {
    api.roles().then((d) => apply(d)).catch((e) => toast.error(e.response?.data?.detail || "Couldn't load roles."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const role = data?.roles.find((r) => r.id === selected);
  const saved = useMemo(() => new Set(role?.permissions || []), [role]);
  const metaDirty = !!role && !role.builtin && (meta.label.trim() !== role.label || meta.description.trim() !== (role.description || ""));
  const dirty = !!draft && !!role && (metaDirty || draft.size !== saved.size || [...draft].some((k) => !saved.has(k)));
  const locked = !!role?.locked;
  const groups = useMemo(() => {
    const out = [];
    for (const area of data?.catalog || []) {
      let g = out.find((x) => x.name === area.group);
      if (!g) { g = { name: area.group, areas: [] }; out.push(g); }
      g.areas.push(area);
    }
    return out;
  }, [data]);

  const pick = (id) => {
    if (id === selected) return;
    if (dirty && !window.confirm("Discard your unsaved changes to this role?")) return;
    select(data, id);
    // On a phone the details sit below the role list.
    if (window.innerWidth < 768) setTimeout(() => document.querySelector('[data-testid="role-detail"]')?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  const setLevel = (area, level) => {
    const next = new Set(draft);
    area.actions.forEach((a) => { if (!a.admin_only) next.delete(a.key); });
    presetFor(area, level).forEach((k) => next.add(k));
    setDraft(next);
  };
  const toggle = (key, on) => {
    const next = new Set(draft);
    if (on) next.add(key); else next.delete(key);
    setDraft(next);
  };
  const save = async () => {
    setBusy("save");
    const extra = role.builtin ? {} : { label: meta.label, description: meta.description };
    try { apply(await api.saveRole(role.id, [...draft], extra), role.id); toast.success(`${extra.label || role.label} saved. It applies to everyone with this role straight away.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the role."); }
    finally { setBusy(""); }
  };
  const reset = async () => {
    if (!window.confirm(`Put ${role.label} back to its standard permissions?`)) return;
    setBusy("reset");
    try { apply(await api.resetRole(role.id), role.id); toast.success(`${role.label} is back to its standard permissions.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't reset the role."); }
    finally { setBusy(""); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the ${role.label} role? This can't be undone.`)) return;
    setBusy("delete");
    try { const d = await api.deleteRole(role.id); setSelected(null); apply(d, null); toast.success(`${role.label} deleted.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete the role."); }
    finally { setBusy(""); }
  };
  const created = (d) => {
    setCreating(false);
    apply(d, d.created);
    toast.success("Role created. Choose what it can do, then save.");
  };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="roles-settings">
      <div className="flex items-center gap-2.5 mb-1"><UserCog className="h-5 w-5" style={{ color: CRIMSON }} /><h1 className="text-xl font-semibold font-brand text-slate-800">Roles &amp; Permissions</h1></div>
      <SupportLookOnlyNote />
      <p className="text-sm text-slate-500 mb-5">Choose what each role can see and do. A person with several roles gets everything those roles allow. Changes apply straight away, without anyone signing in again. Give people roles in Authentication &gt; Users.</p>
      {creating && <NewRoleDialog roles={data.roles} onClose={() => setCreating(false)} onCreated={created} />}

      <div className="grid gap-4 md:grid-cols-[240px_minmax(0,1fr)]">
        <nav className="space-y-1" aria-label="Roles" data-testid="roles-list">
          <Button size="sm" variant="outline" className="w-full mb-2" onClick={() => setCreating(true)} disabled={dirty} title={dirty ? "Save or discard your changes first" : undefined} data-testid="role-new">
            <Plus className="h-3.5 w-3.5 mr-1.5" />New role
          </Button>
          {data.roles.map((r, i) => (<div key={r.id}>
            {(i === 0 || data.roles[i - 1].builtin !== r.builtin) && (
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 pt-2 pb-1 px-1">{r.builtin ? "Built-in roles" : "Custom roles"}</h2>
            )}
            <button key={r.id} type="button" onClick={() => pick(r.id)} data-testid={`role-${r.id.replace(/\s+/g, "-")}`}
              className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${r.id === selected ? "border-brand bg-brand/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-slate-800 truncate">{r.label}</span>
                {r.locked && <Lock className="h-3 w-3 text-slate-400 shrink-0" aria-label="Fixed role" />}
                {r.customised && <Badge className="ml-auto border bg-amber-50 text-amber-700 border-amber-200 text-[9px] px-1.5">Changed</Badge>}
              </div>
              <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><Users className="h-3 w-3" />{r.user_count} {r.user_count === 1 ? "person" : "people"}</div>
            </button>
          </div>))}
        </nav>

        {role && (
          <section className="rounded-xl border border-slate-200 bg-white" data-testid="role-detail">
            <div className="p-4 border-b border-slate-100">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  {role.builtin ? (<>
                    <h2 className="text-base font-semibold font-brand text-slate-800">{role.label}</h2>
                    <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
                  </>) : (
                    <div className="space-y-1.5 max-w-md">
                      <Input value={meta.label} onChange={(e) => setMeta((m) => ({ ...m, label: e.target.value }))} maxLength={60}
                        aria-label="Role name" className="h-9 text-sm font-semibold" data-testid="role-name" />
                      <Input value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
                        aria-label="Description" placeholder="What is this role for? (optional)" className="h-8 text-xs" data-testid="role-description" />
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1.5" data-testid="role-members">
                    {role.members.length === 0 ? "Nobody has this role yet."
                      : <>Held by {role.members.slice(0, 6).map((m) => m.name).join(", ")}{role.members.length > 6 ? ` and ${role.members.length - 6} more` : ""}.</>}
                  </p>
                  {role.updated_at && <p className="text-[11px] text-slate-400 mt-1">Last changed by {role.updated_by} · {fmtLog(role.updated_at)}</p>}
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600 shrink-0">
                  Advanced
                  <Switch checked={advanced} onCheckedChange={setAdvanced} data-testid="roles-advanced" />
                </label>
              </div>
              {locked && (
                <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="role-locked-note">
                  <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
                  <span>{role.id === "System Manager"
                    ? "Admin always has full access, including to this screen, so it can't be changed."
                    : "Support Workers use the mobile app, and its access is fixed. This role gives no office access."}</span>
                </div>
              )}
            </div>

            <div className="divide-y divide-slate-100">
              {groups.map((g) => (
                <div key={g.name} className="p-4" data-testid={`group-${g.name.replace(/\s+/g, "-")}`}>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{g.name}</h3>
                  <div className="space-y-3">
                    {g.areas.map((area) => area.toggles ? (
                      <div key={area.key} className="grid sm:grid-cols-2 gap-x-6">
                        {area.actions.map((a) => (
                          <ActionRow key={a.key} action={a} asSwitch checked={draft.has(a.key)} disabled={locked}
                            onToggle={(v) => toggle(a.key, v)} />
                        ))}
                      </div>
                    ) : (
                      <div key={area.key} data-testid={`area-${area.key}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm text-slate-800">{area.label}</div>
                            {area.description && <div className="text-[11px] text-slate-400">{area.description}</div>}
                          </div>
                          <LevelPicker area={area} granted={draft} disabled={locked} onPick={(l) => setLevel(area, l)} />
                        </div>
                        {advanced && (
                          <div className="mt-1.5 ml-1 pl-3 border-l-2 border-slate-100">
                            {area.actions.map((a) => (
                              <ActionRow key={a.key} action={a} checked={draft.has(a.key)} disabled={locked}
                                onToggle={(v) => toggle(a.key, v)} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {!locked && (
              // Buttons sit on the left: the floating Feedback button covers the bottom-right corner.
              <div className="sticky bottom-0 flex flex-wrap items-center gap-2 p-3 pr-36 border-t border-slate-200 bg-white/95 backdrop-blur rounded-b-xl">
                <Button size="sm" style={{ backgroundColor: CRIMSON }} disabled={!dirty || !!busy} onClick={save} data-testid="role-save">
                  {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}Save role
                </Button>
                <Button variant="outline" size="sm" disabled={!dirty || !!busy} onClick={() => select(data, role.id)} data-testid="role-discard">Discard</Button>
                {role.customised && (
                  <Button variant="outline" size="sm" onClick={reset} disabled={!!busy} data-testid="role-reset">
                    {busy === "reset" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}Reset to standard
                  </Button>
                )}
                {!role.builtin && (
                  <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={remove}
                    disabled={!!busy || role.user_count > 0} title={role.user_count > 0 ? "Take this role off everyone who has it first" : undefined} data-testid="role-delete">
                    {busy === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}Delete role
                  </Button>
                )}
                {dirty && <span className="text-[11px] text-amber-700">Unsaved changes</span>}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function NewRoleDialog({ roles, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try { onCreated(await api.createRole({ label, description, copy_from: copyFrom || null })); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the role."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-role-dialog">
        <DialogHeader><DialogTitle>New role</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="new-role-name" className="text-xs font-semibold text-slate-700">Name</Label>
            <Input id="new-role-name" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder="e.g. Payroll Clerk" className="mt-1" data-testid="new-role-name" />
          </div>
          <div>
            <Label htmlFor="new-role-desc" className="text-xs font-semibold text-slate-700">Description (optional)</Label>
            <Textarea id="new-role-desc" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 text-sm min-h-[60px]" data-testid="new-role-description" />
          </div>
          <div>
            <Label htmlFor="new-role-copy" className="text-xs font-semibold text-slate-700">Start from</Label>
            <select id="new-role-copy" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} data-testid="new-role-copy"
              className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="">Nothing (no access)</option>
              {roles.filter((r) => r.id !== "Support Worker").map((r) => <option key={r.id} value={r.id}>A copy of {r.label}</option>)}
            </select>
            <p className="text-[11px] text-slate-400 mt-1">Admin-only actions are never copied.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button style={{ backgroundColor: CRIMSON }} onClick={create} disabled={busy || !label.trim()} data-testid="new-role-create">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Create role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
